import { useMemo } from 'react';
import { VyIconType } from '@vy/ui-design-system';
import { Optional, withDefault, now, raiseUnreachable, ItemOf } from '@vy/utils';
import { GetExportedIntegrationsResponse } from '@vy/api-definition';
import { SYNC_JOB_STATUSES, SyncJobStatus } from '@vy/integrations-types';

type UseIntegrationStatusResult = {
  syncStatus: SyncJobStatus;
  date: Date;
  icon: VyIconType;
  label: string;
  isSynced: boolean;
};

export function useIntegrationStatus(
  exportedIntegration: Optional<ItemOf<GetExportedIntegrationsResponse>>,
): UseIntegrationStatusResult {
  const syncStatus = withDefault(exportedIntegration?.status, SYNC_JOB_STATUSES.PENDING);
  const date = withDefault(exportedIntegration?.updatedAt, now());

  return useMemo(() => {
    return getIntegrationStatusMeta(syncStatus, date);
  }, [syncStatus, date]);
}

function getIntegrationStatusMeta(status: SyncJobStatus, date: Date): UseIntegrationStatusResult {
  return {
    syncStatus: status,
    date,
    icon: getIcon(status),
    label: getLabel(status),
    isSynced: status === SYNC_JOB_STATUSES.COMPLETED,
  };
}

function getLabel(status: SyncJobStatus): string {
  switch (status) {
    case SYNC_JOB_STATUSES.IN_PROGRESS:
    case SYNC_JOB_STATUSES.PENDING:
      return 'NetSuite not synced';
    case SYNC_JOB_STATUSES.FAILED:
      return 'Sync failed';
    case SYNC_JOB_STATUSES.COMPLETED:
      return 'Synced to NetSuite';
    default:
      return raiseUnreachable(status);
  }
}

function getIcon(status: SyncJobStatus): VyIconType {
  switch (status) {
    case SYNC_JOB_STATUSES.IN_PROGRESS:
    case SYNC_JOB_STATUSES.PENDING:
      return 'NotSyncedErpIdle';
    case SYNC_JOB_STATUSES.FAILED:
      return 'FailedErpIdle';
    case SYNC_JOB_STATUSES.COMPLETED:
      return 'SyncedErpIdle';
    default:
      return raiseUnreachable(status);
  }
}
